/**
 * Mutation hooks for the Buxgalteriya (Accounting) module.
 *
 * Eight hooks cover the full state machine surface:
 *
 *   - useCreateTransaction / useUpdateTransaction / useDeleteTransaction
 *   - useAttachInvoice (UNPAID → INVOICED; opens or links a PENDING invoice)
 *   - useCreateInvoice / useUpdateInvoice / useDeleteInvoice
 *   - useMarkInvoicePaid (PENDING → PAID, cascades covered txns → SETTLED)
 *
 * The three TRANSACTION mutations carry full optimistic flow (P2):
 *   - `onMutate` snapshots every transactions-cache variant and patches them
 *     in place via the pure helpers in `model/optimistic-txn.ts`. Create
 *     inserts a placeholder row (id `optimistic-<ts>`, status UNPAID) at the
 *     top of the list; update merges the change into the matching row;
 *     delete drops the row by id. The UI sees the change immediately.
 *   - `onError` restores the snapshots and surfaces the message via toast.
 *   - `onSettled` invalidates the whole `/v1/accounting` prefix so a real
 *     server response replaces the optimistic data on the next render.
 *
 * The five INVOICE-side mutations (attach, createInvoice, updateInvoice,
 * deleteInvoice, markInvoicePaid) are still correct-first / non-optimistic;
 * P3 layers their dual-cache patches on top of this same pattern when the
 * invoice surface ships.
 *
 * Error toasts: the global `MutationCache.onError` (src/shared/api/
 * queryClient.ts, error-toast-dedup MR) now covers mutations WITHOUT a
 * hook-level `onError`. The three optimistic TRANSACTION mutations keep
 * their own `onError` (rollback + toast) — the global handler skips
 * mutations that declare one, so no double toast. The invoice-side
 * mutations' former toast-only `onError: toastError` handlers were removed;
 * their failures toast via the global handler.
 */

import { useMutation, useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query'
import { useEntityInvalidate, type ApiResponse } from '@/shared/api'
import { Toast } from '@/shared/ui/organisms/toast'
import {
  attachInvoice,
  createInvoice,
  createTransaction,
  deleteInvoice,
  deleteTransaction,
  markInvoicePaid,
  updateInvoice,
  updateTransaction,
} from './accounting.api'
import { ACCOUNTING_PREFIX, accountingKeys } from './query-keys'
import { pushTransaction, removeTransaction, replaceTransaction } from '../model/optimistic-txn'
import { applyOptimisticAttach } from '../model/optimistic-pending'
import type {
  AttachInvoicePayload,
  CreateInvoice,
  CreateTransaction,
  Invoice,
  PendingPaymentsResponse,
  Transaction,
  TransactionPage,
  UpdateInvoice,
  UpdateTransaction,
} from './contracts'

const toastError = (err: Error) => Toast({ type: 'error', message: err.message })

// ─── Optimistic infrastructure (transactions only — P3 mirrors for invoices) ──

type TransactionCacheSnapshot = Array<[QueryKey, ApiResponse<TransactionPage> | undefined]>

interface TxnMutationContext {
  snapshots: TransactionCacheSnapshot
}

/**
 * Snapshot every cached transactions list (across all paramsSuffix + lang
 * variants) so a failed mutation can be rolled back without losing the
 * server-side state that was there before the optimistic write. Returns a
 * fresh array so callers' rollback loop stays consistent across renders.
 */
function snapshotTransactionCaches(queryClient: QueryClient): TransactionCacheSnapshot {
  const entries = queryClient.getQueriesData<ApiResponse<TransactionPage>>({
    queryKey: accountingKeys.transactions(),
  })
  return entries.map(([key, data]) => [key, data])
}

/**
 * Restore each cached transactions list to its pre-mutation snapshot. Called
 * from `onError` after the rollback intent is known.
 */
function restoreTransactionCaches(
  queryClient: QueryClient,
  snapshots: TransactionCacheSnapshot,
): void {
  for (const [key, data] of snapshots) {
    queryClient.setQueryData(key, data)
  }
}

/** Build the placeholder `Transaction` shown in the table during the round trip. */
function buildOptimisticTransaction(body: CreateTransaction): Transaction {
  return {
    ...body,
    id: `optimistic-${Date.now()}`,
    status: 'UNPAID',
    invoiceId: null,
    createdBy: 'optimistic',
    createdAt: new Date().toISOString(),
  }
}

// ─── Transactions ────────────────────────────────────────────────────────────

export function useCreateTransaction() {
  const queryClient = useQueryClient()
  const invalidate = useEntityInvalidate(ACCOUNTING_PREFIX)
  return useMutation<Transaction, Error, CreateTransaction, TxnMutationContext>({
    mutationFn: (body) => createTransaction(body),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: accountingKeys.transactions() })
      const snapshots = snapshotTransactionCaches(queryClient)
      const placeholder = buildOptimisticTransaction(body)
      queryClient.setQueriesData<ApiResponse<TransactionPage>>(
        { queryKey: accountingKeys.transactions() },
        (old) => pushTransaction(old, placeholder),
      )
      return { snapshots }
    },
    onError: (err, _vars, context) => {
      if (context) restoreTransactionCaches(queryClient, context.snapshots)
      toastError(err)
    },
    onSettled: () => invalidate(),
  })
}

export function useUpdateTransaction() {
  const queryClient = useQueryClient()
  const invalidate = useEntityInvalidate(ACCOUNTING_PREFIX)
  return useMutation<
    Transaction,
    Error,
    { id: string; body: UpdateTransaction },
    TxnMutationContext
  >({
    mutationFn: ({ id, body }) => updateTransaction(id, body),
    onMutate: async ({ id, body }) => {
      await queryClient.cancelQueries({ queryKey: accountingKeys.transactions() })
      const snapshots = snapshotTransactionCaches(queryClient)
      queryClient.setQueriesData<ApiResponse<TransactionPage>>(
        { queryKey: accountingKeys.transactions() },
        (old) => {
          if (!old?.success || !old.data) return old
          const current = old.data.content.find((t) => t.id === id)
          if (!current) return old
          // Merge user-editable fields with the server-managed envelope so
          // status / invoiceId / createdBy / createdAt stay untouched. The
          // server is authoritative; this preview only mirrors the edit
          // surface the user touched.
          const merged: Transaction = { ...current, ...body }
          return replaceTransaction(old, merged)
        },
      )
      return { snapshots }
    },
    onError: (err, _vars, context) => {
      if (context) restoreTransactionCaches(queryClient, context.snapshots)
      toastError(err)
    },
    onSettled: () => invalidate(),
  })
}

export function useDeleteTransaction() {
  const queryClient = useQueryClient()
  const invalidate = useEntityInvalidate(ACCOUNTING_PREFIX)
  return useMutation<void, Error, { id: string }, TxnMutationContext>({
    mutationFn: ({ id }) => deleteTransaction(id),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: accountingKeys.transactions() })
      const snapshots = snapshotTransactionCaches(queryClient)
      queryClient.setQueriesData<ApiResponse<TransactionPage>>(
        { queryKey: accountingKeys.transactions() },
        (old) => removeTransaction(old, id),
      )
      return { snapshots }
    },
    onError: (err, _vars, context) => {
      if (context) restoreTransactionCaches(queryClient, context.snapshots)
      toastError(err)
    },
    onSettled: () => invalidate(),
  })
}

export function useAttachInvoice() {
  const invalidate = useEntityInvalidate(ACCOUNTING_PREFIX)
  return useMutation<Invoice, Error, { txnId: string; payload: AttachInvoicePayload }>({
    mutationFn: ({ txnId, payload }) => attachInvoice(txnId, payload),
    onSuccess: () => invalidate(),
  })
}

// ─── Invoices ────────────────────────────────────────────────────────────────

// Optimistic envelope snapshot for the pending-payments aggregate cache.
// Mirrors `TransactionCacheSnapshot` but the cached value here is the
// `{summary, groups}` aggregate, not a `Page<T>` envelope.
type PendingPaymentsCacheSnapshot = Array<
  [QueryKey, ApiResponse<PendingPaymentsResponse> | undefined]
>

interface CreateInvoiceMutationContext {
  pendingSnapshots: PendingPaymentsCacheSnapshot
}

function snapshotPendingPaymentsCaches(queryClient: QueryClient): PendingPaymentsCacheSnapshot {
  const entries = queryClient.getQueriesData<ApiResponse<PendingPaymentsResponse>>({
    queryKey: accountingKeys.pendingPayments(),
  })
  return entries.map(([key, data]) => [key, data])
}

function restorePendingPaymentsCaches(
  queryClient: QueryClient,
  snapshots: PendingPaymentsCacheSnapshot,
): void {
  for (const [key, data] of snapshots) {
    queryClient.setQueryData(key, data)
  }
}

/**
 * Create a standalone invoice over an explicit set of UNPAID txns.
 *
 * Optimistic flow:
 *   - `onMutate` snapshots every cached pending-payments aggregate (one per
 *     active locale variant) and patches them in place via the pure helper
 *     `applyOptimisticAttach`. Selected rows vanish from the UNPAID groups
 *     immediately; card #1 / #2 decrement and (when paid:false on EXPENSE)
 *     card #3 increments.
 *   - `onError` restores the snapshots and surfaces the message via toast.
 *   - `onSettled` invalidates the whole `/v1/accounting` prefix so the real
 *     server state replaces the optimistic patch on the next render. The
 *     invoices + transactions + dashboard caches all refresh together
 *     because `useEntityInvalidate(ACCOUNTING_PREFIX)` matches by key
 *     prefix (no `exact: true`).
 */
export function useCreateInvoice() {
  const queryClient = useQueryClient()
  const invalidate = useEntityInvalidate(ACCOUNTING_PREFIX)
  return useMutation<Invoice, Error, CreateInvoice, CreateInvoiceMutationContext>({
    mutationFn: (body) => createInvoice(body),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: accountingKeys.pendingPayments() })
      const pendingSnapshots = snapshotPendingPaymentsCaches(queryClient)
      const kind = body.type === 'EXPENSE' ? 'EXPENSES' : 'INCOME'
      queryClient.setQueriesData<ApiResponse<PendingPaymentsResponse>>(
        { queryKey: accountingKeys.pendingPayments() },
        (old) => {
          if (!old?.success || !old.data) return old
          return {
            ...old,
            data: applyOptimisticAttach(old.data, {
              kind,
              txnIds: body.txnIds,
              paid: body.paid,
            }),
          }
        },
      )
      return { pendingSnapshots }
    },
    onError: (err, _vars, context) => {
      if (context) restorePendingPaymentsCaches(queryClient, context.pendingSnapshots)
      toastError(err)
    },
    onSettled: () => invalidate(),
  })
}

export function useUpdateInvoice() {
  const invalidate = useEntityInvalidate(ACCOUNTING_PREFIX)
  return useMutation<Invoice, Error, { id: string; body: UpdateInvoice }>({
    mutationFn: ({ id, body }) => updateInvoice(id, body),
    onSuccess: () => invalidate(),
  })
}

export function useDeleteInvoice() {
  const invalidate = useEntityInvalidate(ACCOUNTING_PREFIX)
  return useMutation<void, Error, { id: string }>({
    mutationFn: ({ id }) => deleteInvoice(id),
    onSuccess: () => invalidate(),
  })
}

export function useMarkInvoicePaid() {
  const invalidate = useEntityInvalidate(ACCOUNTING_PREFIX)
  return useMutation<Invoice, Error, { id: string }>({
    mutationFn: ({ id }) => markInvoicePaid(id),
    onSuccess: () => invalidate(),
  })
}
